import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';
import { get, isNumber } from 'lodash';
import { withExperiment } from '@sparefoot/react-core/decorators';

import { buildSearchLink, getNearMeHeadline } from 'lib/search';
import { isLarge } from 'utils/breakpoints';
import { withinCallCenterHours as computeCallCenterHours } from 'utils/withinCallCenterHours';
import withViewportWidth from 'decorators/withViewportWidth';
import {
	withTruckPromos,
	withSearchFilterContent,
	withSearchPageHandlers,
	withAdditionalFacilitiesIndicator,
} from 'decorators/search';
import { withSearchHandler, useWebPageSchema } from 'decorators';
import { STORAGE_DEALS_SEARCH_PATH } from 'config/search';

import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { Page, Container, Row, Column } from 'components/core/Page';
import ToastNotification from 'components/core/ToastNotification/ToastNotification';
import { searchSelector } from 'store/selectors/search';
import {
	fetchSearchResults,
	fetchStorageDealsResults,
	mapRedoSearchAction,
} from 'store/search/slice';
import { fetchBestReviews } from 'store/reviews/slice';
import { getFacilityHoursAndDirection } from 'store/facilities/slice';
import {
	getLocalPhoneNumberByZipCode,
	getLocalPhoneNumberByCityState,
	getTollfreePhoneNumber,
} from 'store/pageMeta/slice';
import { Modal, ModalBody } from 'components/core/Modal';
import { SearchRail } from 'components/search/SearchRail';
import { SearchResults } from 'components/search/SearchResults';
import { BackToTop } from 'components/core/BackToTop';

import loadable from '@loadable/component';

const SearchMap = loadable(() => import(/* webpackChunkName: "SearchMap" */ 'components/search/SearchMap'));
const HoursAndDirectionsModal = loadable(() => import(/* webpackChunkName: "HoursAndDirectionsModal" */ 'components/facility/HoursAndDirectionsModal'));
const UnitSizeGuide = loadable(() => import(/* webpackChunkName: "UnitSizeGuide" */ 'components/unit/UnitSizeGuide'));
import { StorageDealsHero } from 'components/landing/StorageDealsHero';
import { CLPHero } from 'components/landing/CLPHero';
import { NewCLPHero } from 'components/landing/NewCLPHero';
import { StickySearchBar } from 'components/landing/StickySearchBar';
import './searchPage.scss';
import { buildFacilityUrl } from 'utils/url';
import { PAGE_SUB_TYPES, PAGE_TYPES } from '@sparefoot/tracking-events';

const sizeBuckets = ['5x10', '10x10', '10x15', '10x20'];

const hash = process.env.REACTFOOT_VERSION; // Extract the hash from the env variable
const cssUrl = `https://philes.sparefoot.com/assets/${hash}/Map.css`;

// Function to dynamically load the CSS
const loadMapCss = (url) => {
	if (typeof window !== 'undefined' && typeof document !== 'undefined') {
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = url;
		link.onload = () => console.log(`Loaded map css on page load`);
		link.onerror = (err) => console.error(`Failed to load map css`, err);
		document.head.appendChild(link);
	}
};

function withSearchPageState(Component) {
	function SearchPageWithState(ownProps) {
		const dispatch = useDispatch();
		const selectorResult = useSelector((state) => searchSelector(state, ownProps));
		const actions = useMemo(
			() => bindActionCreators({
				fetchSearchResults,
				fetchStorageDealsResults,
				fetchBestReviews,
				getFacilityHoursAndDirection,
				getLocalPhoneNumberByZipCode,
				getLocalPhoneNumberByCityState,
				getTollfreePhoneNumber,
				mapRedoSearchAction,
			}, dispatch),
			[dispatch],
		);
		const [callCenterOpen, setCallCenterOpen] = useState(true);
		useEffect(() => { setCallCenterOpen(computeCallCenterHours()); }, []);

		const props = { ...ownProps, ...selectorResult, actions, withinCallCenterHours: callCenterOpen };

		const navigate = useNavigate();
		const [filtersShowing, setFiltersShowing] = useState(false);
		const [mapShowing, setMapShowing] = useState(false);
		const [showHoursForFacility, setShowHoursForFacility] = useState(null);
		const [unitSizeGuideShowing, setUnitSizeGuideShowing] = useState(false);
		const [showToastNotification, setShowToastNotification] = useState(false);
		const [preselectedFacilityId, setPreselectedFacilityId] = useState(null);

		const nearMeHeadline = getNearMeHeadline(props.match);
		const primaryHeadlineFromNearMe = nearMeHeadline ? { primaryHeadline: nearMeHeadline } : {};

		const units = useMemo(() => {
			return sizeBuckets.reduce((unit, size) => {
				const lowestPricedUnit = get(props.searchContent?.pricingStats, ['lowestPrices', size]);
				const averagePrice = get(props.searchContent?.pricingStats, ['averagePrices', size, 'price']);

				if (get(lowestPricedUnit, 'price', null) && averagePrice) {
					return [...unit, {
						...lowestPricedUnit,
						sizeText: size,
						averagePrice,
						reserveUrl: buildFacilityUrl(
							lowestPricedUnit.facility,
							lowestPricedUnit.facilityName,
							lowestPricedUnit?.facilityCity || props.searchContent?.location?.city,
							lowestPricedUnit?.facilityState || props.searchContent?.location?.state
						),
					}];
				}
				return unit;
			}, []);
		}, [props.searchContent]);

		const openFilters = useCallback(() => setFiltersShowing(true), []);
		const closeFilters = useCallback(() => setFiltersShowing(false), []);
		const hideInteractiveMap = useCallback(() => {
			setMapShowing(false);
			setShowToastNotification(props.mapRedoSearch);
		}, [props.mapRedoSearch]);
		const openInteractiveMap = useCallback((facilityId) => {
			props.actions.mapRedoSearchAction(false);
			setMapShowing(true);
			setShowToastNotification(false);
			setPreselectedFacilityId(isNumber(facilityId) && facilityId);
		}, [props.actions]);
		const closeHoursRequest = useCallback(() => setShowHoursForFacility(null), []);
		const openHoursRequest = useCallback((facilityId) => setShowHoursForFacility(facilityId), []);
		const handleShowUnitGuide = useCallback(() => setUnitSizeGuideShowing(true), []);
		const handleHideUnitGuide = useCallback(() => setUnitSizeGuideShowing(false), []);

		const onSearchSuccess = useCallback(() =>
			props.actions.fetchBestReviews(props.facilities.map((facility) => facility.id)),
		[props.actions, props.facilities]);

		const handleSearchParamChange = useCallback((newParams) => {
			setFiltersShowing(false);
			navigate(
				buildSearchLink(
					{ ...props.searchParams, ...newParams, page: 1 },
					props.isStorageDealsPage && STORAGE_DEALS_SEARCH_PATH
				)
			);
			return { queryParams: newParams };
		}, [navigate, props.searchParams, props.isStorageDealsPage]);

		const handleVIPFacilityMapOpen = useCallback(() => {
			openInteractiveMap(props.vipFacilityId);
		}, [openInteractiveMap, props.vipFacilityId]);

		useEffect(() => {
			loadMapCss(cssUrl);
			onSearchSuccess();
			if (
				props.queryParams?.subPageType === 'nearMePages' &&
				props.location?.pathname === '/search.html'
			) {
				props.actions.fetchSearchResults(props.queryParams)
					.then(() => onSearchSuccess());
			}
		}, []); // eslint-disable-line react-hooks/exhaustive-deps

		return (
			<Component
				{...props}
				{...primaryHeadlineFromNearMe}
				units={units}
				filtersShowing={filtersShowing}
				mapShowing={mapShowing}
				showHoursForFacility={showHoursForFacility}
				unitSizeGuideShowing={unitSizeGuideShowing}
				showToastNotification={showToastNotification}
				preselectedFacilityId={preselectedFacilityId}
				openFilters={openFilters}
				closeFilters={closeFilters}
				hideInteractiveMap={hideInteractiveMap}
				openInteractiveMap={openInteractiveMap}
				closeHoursRequest={closeHoursRequest}
				openHoursRequest={openHoursRequest}
				handleShowUnitGuide={handleShowUnitGuide}
				handleHideUnitGuide={handleHideUnitGuide}
				onSearchSuccess={onSearchSuccess}
				handleSearchParamChange={handleSearchParamChange}
				handleVIPFacilityMapOpen={handleVIPFacilityMapOpen}
			/>
		);
	}
	SearchPageWithState.displayName = 'SearchPageWithState';
	return SearchPageWithState;
}

export const enhance = (Component) =>
	withViewportWidth(
		withSearchPageState(
			withSearchPageHandlers(
				withTruckPromos(
					withSearchHandler(
						withAdditionalFacilitiesIndicator(
							withPageViewTracking(PAGE_TYPES.SEARCH)(
								withExperiment('sf_12496_sfdc_clp_unitcardrefresh')(
									withSearchFilterContent(Component)
								)
							)
						)
					)
				)
			)
		)
	);

export function SearchPage(props) {
	const {
		actions,
		filtersShowing,
		filterComponent,
		handleSearchParamChange,
		handleHideUnitGuide,
		injectAllEvenIfNoContent,
		injectedComponents,
		isTruckSerp,
		listType,
		openFilters,
		closeFilters,
		queryParams,
		openInteractiveMap,
		hideInteractiveMap,
		mapShowing,
		showHoursForFacility,
		openHoursRequest,
		closeHoursRequest,
		unitSizeGuideShowing,
		showVehicleLengthTool,
		viewportWidth,
		searchParams,
		searchLocation,
		searchId,
		searchPath,
		primaryHeadline,
		secondaryHeadline,
		totalFacilities,
		facilities,
		locationLabel,
		searchTerm,
		searchType,
		page,
		canonicalUrl,
		cityUrl,
		facilitiesPerPage,
		loading,
		localPhone,
		stateUrl,
		searchContent,
		numPages,
		irmFacilities,
		moveInDate,
		phone,
		handleSearch,
		faqContent,
		faqShowing,
		showToastNotification,
		firstListingIsVipEligible,
		vipAccount,
		vipFacilityId,
		vipFacilityImages,
		preselectedFacilityId,
		handleVIPFacilityMapOpen,
		isStorageDealsPage,
		fmfFacilitiesCount,
		promoFacilitiesCount,
		pageType,
		baseUrl,
		sf12496Variation, // sf_12496_sfdc_clp_unitcardrefresh
		units,
		match,
	} = props;
	const schemaProps = useWebPageSchema(props);
	const { clpPageSchema, clpMapSchema, pageSchema } = schemaProps;
	const showInformedSearch =
		!searchParams.informedSearch &&
		searchParams.subPageType === 'cityLandingPage';
	const isCLPRedesign = sf12496Variation === 'variation'; // sf_12496_sfdc_clp_unitcardrefresh

	const isNearMePage = match?.url?.startsWith('/near-me/');
	const hasNonPaginationParams = isNearMePage && queryParams &&
		Object.keys(queryParams).some((p) => p !== 'page');

	return (
		<Page
			pageType={pageType}
			className={classNames('search-page', {
				'truck-search-page': isTruckSerp,
			})}
		>
			{hasNonPaginationParams && (
				<Helmet>
					<meta
						name="robots"
						content="noindex, nofollow"
						key="nmp-filtered-robots"
					/>
				</Helmet>
			)}
			{(searchParams.subPageType === 'cityLandingPage' ||
				searchParams.subPageType === PAGE_SUB_TYPES.AMENITY_LANDING ||
				searchParams.subPageType ===
					PAGE_SUB_TYPES.VEHICLE_LANDING) && (
				<Helmet>
					{!!(
						searchParams.informedSearch ||
						(match &&
							match.url &&
							match.url.includes('/search.html'))
					) && (
						<meta
							name="robots"
							content="noindex, nofollow"
							key="search-page-robots"
						/>
					)}
					<meta
						name="msvalidate.01"
						content="72430AA401040F456AF99A586F12B284"
					/>
					{clpPageSchema && (
						<script type="application/ld+json">
							{clpPageSchema}
						</script>
					)}
					<script type="application/ld+json">
						{`
							{
								"@context": "http://schema.org",
								"@type": "WebSite",
								"url": "https://www.sparefoot.com/",
								"potentialAction": {
									"@type": "SearchAction",
									"target": "https://www.sparefoot.com/search?location={search_term_string}",
									"query-input": "required name=search_term_string"
								}
							}
						`}
					</script>
				</Helmet>
			)}
			{showInformedSearch &&
				(!isCLPRedesign ? (
					<CLPHero
						searchLocation={searchLocation}
						queryParams={queryParams}
						headline={primaryHeadline}
						subtitle={secondaryHeadline}
					/>
				) : (
					<NewCLPHero
						queryParams={queryParams}
						headline={primaryHeadline}
						subtitle={secondaryHeadline}
					/>
				))}

			{showToastNotification && (
				<ToastNotification text="Your search results have been updated based on your map results." />
			)}
			{isStorageDealsPage && (
				<>
					<StorageDealsHero
						promoFacilitiesCount={promoFacilitiesCount}
						fmfFacilitiesCount={fmfFacilitiesCount}
						facilities={facilities}
						viewportWidth={viewportWidth}
					/>
					<StickySearchBar
						handleSearch={handleSearch}
						searchPath="/storage-deals.html"
					/>
				</>
			)}
			<div className="search-container">
				<Container gutters="large">
					<Row reverse>
						<Column
							large={8}
							xlarge={9}
						>
							<SearchResults
								canonicalUrl={canonicalUrl}
								cityUrl={cityUrl}
								facilities={facilities}
								facilitiesPerPage={facilitiesPerPage}
								locationLabel={locationLabel}
								queryParams={queryParams}
								searchTerm={searchTerm}
								searchType={searchType}
								page={page}
								primaryHeadline={primaryHeadline}
								searchLocation={searchLocation}
								searchParams={searchParams}
								secondaryHeadline={secondaryHeadline}
								totalFacilities={totalFacilities}
								loading={loading}
								localPhone={localPhone}
								stateUrl={stateUrl}
								searchContent={searchContent}
								numPages={numPages}
								getLocalPhoneNumberByZipCode={
									actions.getLocalPhoneNumberByZipCode
								}
								getLocalPhoneNumberByCityState={
									actions.getLocalPhoneNumberByCityState
								}
								getTollfreePhoneNumber={
									actions.getTollfreePhoneNumber
								}
								irmFacilities={irmFacilities}
								moveInDate={moveInDate}
								phone={phone}
								openInteractiveMap={openInteractiveMap}
								openHoursRequest={openHoursRequest}
								openFilters={openFilters}
								handleSearchParamChange={
									handleSearchParamChange
								}
								injectedComponents={injectedComponents}
								injectAllEvenIfNoContent={
									injectAllEvenIfNoContent
								}
								listType={listType}
								showVehicleLengthTool={showVehicleLengthTool}
								handleSearch={handleSearch}
								searchId={searchId}
								isStorageDealsPage={isStorageDealsPage}
								fmfFacilitiesCount={fmfFacilitiesCount}
								promoFacilitiesCount={promoFacilitiesCount}
								firstListingIsVipEligible={
									firstListingIsVipEligible
								}
								vipAccount={vipAccount}
								vipFacilityId={vipFacilityId}
								vipFacilityImages={vipFacilityImages}
								vipShowMapHandler={handleVIPFacilityMapOpen}
								// this will be moved outside when refactoring search results
								faqContent={faqContent}
								faqShowing={faqShowing}
								isCLP={
									!searchParams.informedSearch &&
									!(match && match.url && match.url.includes('/search.html'))
								}
								baseUrl={baseUrl}
								searchPath={searchPath}
								bestPricedUnits={units}
								isCLPRedesign={isCLPRedesign} // sf_12496_sfdc_clp_unitcardrefresh
							/>
						</Column>
						<Column
							large={4}
							xlarge={3}
						>
							<SearchRail
								queryParams={queryParams}
								showing={mapShowing}
								closeMap={hideInteractiveMap}
								onSearch={handleSearchParamChange}
								openInteractiveMap={openInteractiveMap}
								closeFilters={closeFilters}
								filtersShowing={filtersShowing}
								onSearchParamChange={handleSearchParamChange}
								isLarge={isLarge(viewportWidth)}
								handleSearch={handleSearch}
								informedSearch={searchParams.informedSearch}
								isCLPRedesign={isCLPRedesign} // sf_12496_sfdc_clp_unitcardrefresh
							>
								{filterComponent}
							</SearchRail>
						</Column>
					</Row>
				</Container>
			</div>
			{!!showHoursForFacility && (
				<HoursAndDirectionsModal
					facilityId={showHoursForFacility}
					showing={!!showHoursForFacility}
					onClose={closeHoursRequest}
					onSubmit={actions.getFacilityHoursAndDirection}
				/>
			)}
			<Modal
				showing={unitSizeGuideShowing}
				onClose={handleHideUnitGuide}
			>
				<ModalBody>
					<UnitSizeGuide />
				</ModalBody>
			</Modal>
			{mapShowing && (
				<SearchMap
					zoom={11}
					queryParams={queryParams}
					showSpinner={false}
					showing={mapShowing}
					closeMap={hideInteractiveMap}
					onSearch={handleSearchParamChange}
					searchOnMove
					preselectedFacilityId={preselectedFacilityId}
				/>
			)}
			<BackToTop />
			<script
				key="schema"
				type="application/ld+json"
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: clpMapSchema }}
			/>
		</Page>
	);
}

SearchPage.propTypes = {
	actions: PropTypes.shape({
		getFacilityHoursAndDirection: PropTypes.func,
		getLocalPhoneNumberByZipCode: PropTypes.func,
		getLocalPhoneNumberByCityState: PropTypes.func,
		getTollfreePhoneNumber: PropTypes.func,
	}),
	openFilters: PropTypes.func,
	closeFilters: PropTypes.func,
	filtersShowing: PropTypes.bool,
	handleSearchParamChange: PropTypes.func,
	handleHideUnitGuide: PropTypes.func,
	isTruckSerp: PropTypes.bool,
	queryParams: PropTypes.object,
	mapShowing: PropTypes.bool,
	filterComponent: PropTypes.node.isRequired,
	hideInteractiveMap: PropTypes.func,
	openInteractiveMap: PropTypes.func,
	showHoursForFacility: PropTypes.number,
	openHoursRequest: PropTypes.func,
	closeHoursRequest: PropTypes.func,
	unitSizeGuideShowing: PropTypes.bool,
	injectedComponents: PropTypes.object,
	injectAllEvenIfNoContent: PropTypes.bool,
	showVehicleLengthTool: PropTypes.bool,
	listType: PropTypes.oneOf(['grid', 'column']),
	viewportWidth: PropTypes.number.isRequired,
	searchParams: PropTypes.shape({
		subPageType: PropTypes.string,
		informedSearch: PropTypes.bool,
	}).isRequired,
	primaryHeadline: PropTypes.string.isRequired,
	secondaryHeadline: PropTypes.string.isRequired,
	totalFacilities: PropTypes.number,
	searchLocation: PropTypes.object,
	searchId: PropTypes.string,
	handleSearch: PropTypes.func.isRequired,
	canonicalUrl: PropTypes.string,
	staticMapUrl: PropTypes.string,
	cityUrl: PropTypes.string,
	facilities: PropTypes.arrayOf(PropTypes.object),
	locationLabel: PropTypes.string,
	searchTerm: PropTypes.string,
	searchType: PropTypes.string,
	page: PropTypes.number.isRequired,
	facilitiesPerPage: PropTypes.number,
	loading: PropTypes.bool,
	localPhone: PropTypes.object,
	stateUrl: PropTypes.string,
	searchContent: PropTypes.object,
	numPages: PropTypes.number,
	irmFacilities: PropTypes.arrayOf(PropTypes.object),
	moveInDate: PropTypes.instanceOf(Date),
	phone: PropTypes.object,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	showToastNotification: PropTypes.bool,
	firstListingIsVipEligible: PropTypes.bool,
	vipAccount: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
	vipFacilityId: PropTypes.number,
	vipFacilityImages: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			atl: PropTypes.string,
		})
	),
	preselectedFacilityId: PropTypes.number,
	handleVIPFacilityMapOpen: PropTypes.func,
	isStorageDealsPage: PropTypes.bool,
	fmfFacilitiesCount: PropTypes.number,
	promoFacilitiesCount: PropTypes.number,
	pageType: PropTypes.string,
	clpPageSchema: PropTypes.string,
	pageSchema: PropTypes.string,
	clpMapSchema: PropTypes.string,
	baseUrl: PropTypes.string,
	searchPath: PropTypes.string,
	units: PropTypes.arrayOf(
		PropTypes.shape({
			averagePrice: PropTypes.number,
			facility: PropTypes.number,
			facilityCity: PropTypes.string,
			facilityImage: PropTypes.string,
			facilityName: PropTypes.string,
			facilityState: PropTypes.string,
			inNetwork: PropTypes.bool,
			price: PropTypes.string,
			reserveUrl: PropTypes.string,
			sizeText: PropTypes.string,
			unit: PropTypes.number,
			squareFootage: PropTypes.number,
		})
	),
	sf12496Variation: PropTypes.string,
	sf13810Variation: PropTypes.string,
	match: PropTypes.object,
};

export default enhance(SearchPage);
